<template>
  <v-card>
    <v-card-title class="v-toolbar__title black--text font-weight-medium pt-8 bg-custom-light">
      {{ $t('Widgets.installation') }}
      <v-spacer />
      <v-btn class="dialog-close-btn" text @click="$emit('close-installation-modal')">
        <v-icon color="grey darken-1">mdi-close </v-icon>
      </v-btn>
    </v-card-title>
    <v-divider />
    <v-card-text>
      <div class="black--text mt-4">
        {{ $t('Widgets.selectAnInstallation') }}
      </div>
      <div v-if="themeStatusLoading" class="text-center mt-4">
        <v-progress-circular indeterminate color="primary" size="24" width="2" />
      </div>
      <template v-else>
        <template v-if="resolvedAppBlockLinks.length">
          <p-alert v-if="allAppBlocksAlreadyOnTheme" variant="info" class="mt-4">
            {{ $t('Widgets.appBlockAlreadyOnTheme') }}
          </p-alert>
          <p-alert v-else-if="someAppBlocksAlreadyOnTheme" variant="info" class="mt-4">
            {{ $t('Widgets.appBlockSomeAlreadyOnTheme') }}
          </p-alert>
          <installation-dialog-element
            :title="$t('Widgets.installViaAppBlock')"
            :subtitle="$t('Widgets.installViaAppBlockSubtitle')"
            use-case="via_app_block"
            :app-block-links="resolvedAppBlockLinks"
            class="mt-4"
            @close-dialog="$emit('close-installation-modal')"
          />
        </template>
        <p-alert v-else variant="info" class="mt-4">
          {{ $t('Widgets.appBlockLinksUnavailable') }}
        </p-alert>
      </template>

      <installation-dialog-element
        :title="$t('Widgets.requestOpinewInstallation')"
        :subtitle="$t('Widgets.contactOurSupportEngineers')"
        :button-label="$t('Widgets.liveChat')"
        use-case="request_installation"
        class="mt-4"
        image-url="https://cdn.opinew.com/dashboard/images/livechat_icon.svg"
        @close-dialog="$emit('close-installation-modal')"
      />

      <v-expansion-panels class="mt-4 other-methods-panels">
        <v-expansion-panel>
          <v-expansion-panel-header class="font-weight-medium black--text px-0">
            {{ $t('Widgets.otherInstallMethods') }}
          </v-expansion-panel-header>
          <v-expansion-panel-content class="px-0">
            <p-text variant="bodyMd" as="p" class="mb-3 gray--text">
              {{ $t('Widgets.otherInstallMethodsDesc') }}
            </p-text>
            <installation-dialog-element
              :title="$t('Widgets.installViaEditor')"
              :subtitle="$t('Widgets.installOpinewSnippets')"
              :button-label="$t('Widgets.editCode')"
              :installation-instructions="installationInstructions"
              use-case="via_editor"
              image-url="https://cdn.opinew.com/dashboard/images/code_icon.svg"
              class="mt-2"
              @close-dialog="$emit('close-installation-modal')"
            />
            <installation-dialog-element
              :title="$t('Widgets.emailInstructionsTo')"
              :subtitle="$t('Widgets.sendInstallationInstructions')"
              :placeholder="$t('Widgets.addEmail')"
              :button-label="$t('Widgets.send')"
              use-case="send_email"
              image-url="https://cdn.opinew.com/dashboard/images/email_icon.svg"
              class="mt-4"
              :instructions-url="instructionsUrl"
              :widget-name="widgetName"
            />
            <installation-dialog-element
              :title="$t('Widgets.installViaBuilder')"
              :subtitle="$t('Widgets.ifYoureUsing')"
              :button-label="$t('Widgets.goToInstructions')"
              use-case="via_builder"
              image-url="https://cdn.opinew.com/dashboard/images/pagebuilder_icon.svg"
              class="mt-4"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-row no-gutters class="mt-8">
        <v-col cols="12">
          <div class="d-flex justify-end">
            <v-btn
              color="black_lighten2"
              class="btn-primary-action"
              outlined
              @click="$emit('close-installation-modal')"
            >
              <span class="black--text"> {{ $t('Widgets.cancel') }} </span>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex';
import InstallationDialogElement from '@/components/widgets/InstallationDialogElement';
import PAlert from '@/components/polaris/PAlert';
import PText from '@/components/polaris/PText';
import {
  getThemeAppBlockLinks,
  linksThatWouldNotDuplicate,
} from '@/helpers/themeAppBlockDeepLinks';
import { fetchThemeAppExtensionStatuses } from '@/helpers/themeAppExtensionStatus';

export default {
  name: 'InstallationModal',
  components: {
    InstallationDialogElement,
    PAlert,
    PText,
  },
  props: {
    widgetName: {
      type: String,
      default: '',
    },
    widgetKey: {
      type: String,
      default: '',
    },
    instructionsUrl: {
      type: String,
      default: '',
    },
    installationInstructions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      themeStatusLoading: true,
      themeStatusAvailable: false,
      themeBlocks: [],
      visibilityObserver: null,
    };
  },
  computed: {
    ...mapState(['accountInfo']),
    appBlockLinks() {
      return getThemeAppBlockLinks({
        widgetKey: this.widgetKey,
        shopDomain: this.accountInfo.shopDomain,
        apiKey: process.env.VUE_APP_SHOPIFY_PUBLIC_API_KEY,
        t: this.$t.bind(this),
      });
    },
    resolvedAppBlockLinks() {
      return linksThatWouldNotDuplicate(
        this.appBlockLinks,
        this.themeBlocks,
        this.themeStatusAvailable,
        {
          shopDomain: this.accountInfo.shopDomain,
          openLabel: this.$t('Widgets.openInThemeEditor'),
        }
      );
    },
    allAppBlocksAlreadyOnTheme() {
      return (
        this.themeStatusAvailable &&
        this.resolvedAppBlockLinks.length > 0 &&
        this.resolvedAppBlockLinks.every(link => link.alreadyOnTheme)
      );
    },
    someAppBlocksAlreadyOnTheme() {
      return (
        this.themeStatusAvailable &&
        this.resolvedAppBlockLinks.some(link => link.alreadyOnTheme) &&
        this.resolvedAppBlockLinks.some(link => !link.alreadyOnTheme)
      );
    },
  },
  mounted() {
    this.loadThemeStatus();
    if (typeof IntersectionObserver !== 'undefined' && this.$el) {
      this.visibilityObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadThemeStatus();
        }
      });
      this.visibilityObserver.observe(this.$el);
    }
  },
  beforeDestroy() {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
  },
  methods: {
    async loadThemeStatus() {
      this.themeStatusLoading = true;
      try {
        const { available, blocks } = await fetchThemeAppExtensionStatuses();
        this.themeStatusAvailable = available;
        this.themeBlocks = blocks || [];
      } catch (_error) {
        this.themeStatusAvailable = false;
        this.themeBlocks = [];
      } finally {
        this.themeStatusLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.bg-custom-light {
  background: #f3f3f3;
  padding: 16px !important;
}
.dialog-close-btn {
  position: relative;
  height: 30px !important;
  min-width: 30px !important;
  padding: 0 !important;
}
.dialog-close-btn::before {
  background: transparent !important;
}
.dialog-close-btn:hover::after {
  content: '';
  position: absolute;
  inset: 2px;
  border-radius: 25%;
  background-color: rgba(138, 138, 138, 0.2);
}
.dialog-close-btn i {
  font-size: 14px;
}
.dialog-close-btn:hover .v-icon {
  color: #303030 !important;
}
.btn-primary-action.v-btn.v-size--default {
  font-size: 13px !important;
  border-radius: 8px;
  height: 32px;
}
.other-methods-panels {
  border: 1px solid #e5e7e9;
  border-radius: 10px;
}
.other-methods-panels ::v-deep .v-expansion-panel {
  background: transparent;
}
.other-methods-panels ::v-deep .v-expansion-panel-header {
  min-height: 48px;
  padding-left: 16px !important;
  padding-right: 16px !important;
}
.other-methods-panels ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 16px 16px;
}
</style>
